/**
 * 명함 이미지 올리기 (2026-10-06 사용자 지시 6 — 「명함의 이미지를 수정할 수 있도록」).
 *
 * ## ⭐ 꼴을 빌렸다 — 올리는 길을 새로 짓지 않았다
 *
 * `api/files.ts` 의 `uploadChatAttachment` 가 **presign → MinIO PUT → finalize** 세 걸음을
 * 이미 한 번에 하고, 실패 문구도 이미 한국어로 바꿔 준다(415·400·413·403). 부르는 제품
 * 파일이 넷이다(`useAttachmentUpload` · `upload-editor-image` · `TaskCreateModal` ·
 * `TaskDetailModal`). ⇒ 그 감싸개를 그대로 쓴다.
 *
 * ⛔ `AvatarUploader` 쪽 길은 **빌릴 수 없다.** 그것은 `PUT /users/me/avatar` 로 서버가 대리
 *    저장하는 별도 끝점이고 **`files.id` 를 주지 않는다.** 명함은 `files.id` 가 필요하다.
 *
 * ## ⛔⛔ 올리기 «전»에 mime 을 본다 — 고아 파일을 막는 유일한 자리
 *
 * 서버 `PATCH /contacts/:id` 는 붙일 때 `isUsablePhoto` 로 잰다. 그 여섯 조건 중 다섯은
 * 내가 올린 파일이면 **구조적으로 참**이다(그 사람이 올린 · 그 기관의 · 안 지워진 ·
 * `verified` · `pending` 아님 — 전부 `uploadChatAttachment` 가 끝내 준다).
 * ⇒ ★ **실제로 걸릴 수 있는 것은 `isInlineSafeImageMime` 하나**다.
 * ⇒ ⇒ 그래서 그것을 **올리기 전에** 본다. 안 보면 「올라갔는데 안 붙은」 고아 파일이 남는다.
 *
 * ⚠ 목록은 서버 SSOT 와 **같은 넷**이다 — `chat-server/src/lib/minio.ts` 의
 *   `INLINE_SAFE_IMAGE_MIME = new Set(['image/jpeg','image/png','image/gif','image/webp'])`.
 *   ⛔ **`image/svg+xml` 은 일부러 빠져 있다**(그 파일 주석: stored XSS — svg 안 script·onload).
 *   ⛔ 그러니 `lib/attachments.ts` 의 `isImageMime`(= `mime.startsWith('image/')`)을
 *      **쓰면 안 된다** — svg 가 통과해 서버가 400 을 내고 고아 파일이 생긴다.
 *
 * ⚠ 이 레포에 같은 넷이 이미 한 곳 있다 — `components/AvatarUploader/AvatarUploader.tsx:18`
 *   (`const ACCEPT`, export 가 아니다). 그리고 **더 좁은** 목록이 셋 더 있다(gif 빠짐:
 *   `employees-shared.ts:91` `AVATAR_MIME` · `PersonHead.tsx:62` · `CompanyLogoGroup.tsx:28`)
 *   와 둘만 받는 곳 하나(`SignatureModal.tsx:36`).
 *   ⇒ ⛔ 그것들을 **합치지 않았다.** 자리마다 받고 싶은 것이 다를 수 있고(서명을 gif 로 받을
 *     까닭이 없다), 남의 화면 동작을 이 판에서 바꾸는 것은 회귀다. 대신 **여기서 서버 SSOT 를
 *     가리키고** 아래 시험이 「정확히 그 넷인가」를 잠근다.
 */

import { uploadChatAttachment } from '../../api/files';

/** 서버 `INLINE_SAFE_IMAGE_MIME` 과 **같은 넷**. 늘리려면 서버를 먼저 고친다. */
export const CONTACT_PHOTO_MIME = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const;

/** `<input type="file" accept>` 에 넣는 꼴. */
export const CONTACT_PHOTO_ACCEPT = CONTACT_PHOTO_MIME.join(',');

/**
 * 이 파일을 명함 이미지로 쓸 수 있나 — **올리기 전에** 묻는다.
 *
 * ⚠ `file.type` 은 브라우저가 확장자로 짐작한 값이라 빌 수 있다. 비면 **거절**한다 —
 *   올려 보고 서버에게 물으면 그때는 이미 고아 파일이다.
 * ⚠ `image/jpeg; charset=x` 처럼 파라미터가 붙어 올 수 있어 세미콜론 앞만 보고 소문자로 맞춘다
 *   (서버 `isInlineSafeImageMime` 이 하는 것과 같다).
 */
export function isContactPhotoMime(mime: string | null | undefined): boolean {
  if (!mime) return false;
  const head = mime.toLowerCase().split(';')[0]!.trim();
  return (CONTACT_PHOTO_MIME as readonly string[]).includes(head);
}

/** `isContactPhotoMime` 이 거절했을 때 던지는 것 — 화면이 이것을 알아보고 제 문구를 쓴다. */
export class ContactPhotoTypeError extends Error {
  constructor() {
    super('contact_photo_bad_type');
    this.name = 'ContactPhotoTypeError';
  }
}

/**
 * 파일 하나를 올려 `files.id` 를 받는다. 붙이는 것(PATCH)은 **부르는 쪽**이 한다.
 *
 * ★ 둘을 갈라 둔 까닭: 올리기가 되고 붙이기가 400 이면 **고아 파일**이 남는다. 화면이
 *   「올라갔는데 안 붙었다」를 사용자에게 다르게 말해야 하므로, 한 함수로 묶지 않는다.
 */
export async function uploadContactPhoto(file: File): Promise<string> {
  if (!isContactPhotoMime(file.type)) throw new ContactPhotoTypeError();
  const uploaded = await uploadChatAttachment(file);
  return uploaded.fileId;
}
